import { Link } from "react-router-dom";
import { ArrowUpRight, Layers, Droplets, Zap, Settings, Building2, Boxes, CheckCircle2, Cpu, Ruler, Users, Package } from "lucide-react";
import CTAStrip from "../components/CTAStrip";
import { services as staticServices } from "../lib/siteConfig";
import { usePublicData } from "../hooks/usePublicData";
import { useLanguage } from "../context/LanguageContext";
import { pub } from "../lib/pubThemeClasses";
import { pickServiceDisplay } from "../lib/cmsI18n";

const iconMap = { Layers, Droplets, Zap, Settings, Building2, Boxes, Cpu, Ruler, Users, Package };

const SERVICE_DETAIL_IDS = ["fdm", "resin", "prototype", "functional", "architecture", "batch"];

export default function Services() {
  const { services } = usePublicData();
  const { t, language } = useLanguage();
  const SV = services?.length ? services : staticServices;

  return (
    <div>
      <section className={`relative border-b bg-grain ${pub.borderSubtle}`}>
        <div className="absolute inset-0 grid-lines opacity-50 pointer-events-none" />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-28">
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#FF4F00] mb-6 flex items-center gap-3">
            <span className="w-8 h-px bg-[#FF4F00]" /> {t("services.eyebrow")}
          </div>
          <h1 className={`font-display text-5xl sm:text-6xl md:text-7xl font-black uppercase tracking-tight leading-[0.9] max-w-4xl ${pub.heroHeading}`}>
            {t("services.hero.line1")}
            <br />
            <span className="text-[#FF4F00]">{t("services.hero.highlight")}</span>
          </h1>
          <p className={`mt-6 text-lg max-w-2xl ${pub.muted}`}>{t("services.hero.sub")}</p>
        </div>
      </section>

      <section className={`border-b ${pub.borderSubtle}`}>
        <div className={`mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-28 space-y-px rounded-sm overflow-hidden border ${pub.gridHairline} ${pub.borderSubtle}`}>
          {SV.map((s, idx) => {
            const Icon = iconMap[s.icon] || Layers;
            const keyId = SERVICE_DETAIL_IDS.includes(String(s.key)) ? s.key : null;
            const sd = pickServiceDisplay(s, language);
            return (
              <div key={s.key || s.id || idx} className="bg-surface-2 p-8 md:p-12 grid grid-cols-1 md:grid-cols-12 gap-10 group hover:bg-surface-3 transition-colors">
                <div className="md:col-span-1 font-mono text-sm text-[#FF4F00]">{String(idx + 1).padStart(2, "0")}</div>
                <div className="md:col-span-4 flex items-start gap-4">
                  <div className={`w-12 h-12 border grid place-items-center text-neutral-900 dark:text-white transition-colors group-hover:border-orange-500/80 dark:group-hover:border-orange-400/55 ${pub.borderSubtle}`}>
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className={`font-display text-2xl md:text-3xl font-black uppercase tracking-tight ${pub.heroHeading}`}>{sd.title}</h3>
                    <p className={`mt-3 text-sm ${pub.muted}`}>{sd.desc}</p>
                  </div>
                </div>
                <ul className="md:col-span-5 space-y-3">
                  {keyId
                    ? [0, 1, 2].map((i) => (
                        <li key={i} className={`flex items-start gap-3 text-sm ${pub.muted}`}>
                          <CheckCircle2 className="w-4 h-4 text-[#FF4F00] mt-0.5 shrink-0" />
                          {t(`services.detail.${keyId}.${i}`)}
                        </li>
                      ))
                    : null}
                </ul>
                <div className="md:col-span-2 flex md:justify-end">
                  <Link
                    to="/teklif-al"
                    data-testid={`services-quote-${s.key}`}
                    className={`inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[#FF4F00] transition-colors ${pub.hoverText}`}
                  >
                    {t("quote.submit.cta")} <ArrowUpRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <CTAStrip />
    </div>
  );
}
